import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Camera, Play, Pause, ArrowRight, ChevronRight, ExternalLink,
  Route as RouteIcon, Radio, Droplets, Hammer, Shield, Building2, Leaf,
  Activity,
} from "lucide-react";
import usePageview from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import {
  SITE_PROFILES, classify, buildBrief, fmtNum,
} from "./IndustrialConnectorDemo";

// Phase 25.16f — Ventia 7-Sector LIVE Dashboard.
//
// Single page that runs all 7 Ventia sectors as live tiles simultaneously.
// Each tile is its own mini-simulator drawing from SITE_PROFILES, ticking
// every 1.5s, producing its own sector-specific brief via the per-profile
// narrator that already powers /industrial-demo.
//
// The intent: when Shaun forwards the bespoke proposal internally, the
// next click after /ventia is THIS page — and what loads is not slideware,
// it's seven simultaneous live ops feeds with anomalies flagging in
// real-time. The proof density that makes "show, don't tell" work.

// Map each Ventia sector code to the profile it consumes + the lucide
// icon shown in the tile header. Order matches the bespoke proposal page.
const SECTOR_TILES = [
  { sector: "01", profileId: "transport",     Icon: RouteIcon,  accent: "#FFB000" },
  { sector: "02", profileId: "telco",         Icon: Radio,      accent: "#FFB000" },
  { sector: "03", profileId: "water",         Icon: Droplets,   accent: "#FFB000" },
  { sector: "04", profileId: "ironore",       Icon: Hammer,     accent: "#FFB000" },
  { sector: "05", profileId: "defence",       Icon: Shield,     accent: "#FFB000" },
  { sector: "06", profileId: "social",        Icon: Building2,  accent: "#FFB000" },
  { sector: "07", profileId: "environmental", Icon: Leaf,       accent: "#FFB000" },
];

// One mini-simulator per tile. We keep state per-tile flat in a Map keyed
// by profileId so a single tick loop can step everything in parallel
// without 7 separate intervals.
function freshTagState(p) {
  return p.tags.map(t => ({
    ...t,
    value: t.baseline,
    pct: 0,
    flag: classify(0),
    injectedTicks: 0,
    injectedDirection: 1,
  }));
}

function tickTags(tagsArr) {
  return tagsArr.map(t => {
    const driftMag = (Math.random() - 0.5) * t.range * 0.3;
    const occasionalSpike = Math.random() < 0.07 ? (Math.random() - 0.5) * t.range * 2.2 : 0;
    let injectionOffset = 0;
    let nextInjectedTicks = t.injectedTicks;
    if (t.injectedTicks > 0) {
      injectionOffset = t.injectedDirection * t.baseline * 0.35;
      nextInjectedTicks = t.injectedTicks - 1;
    }
    const nextValue = t.value + driftMag + occasionalSpike + injectionOffset;
    const revertStrength = nextInjectedTicks > 0 ? 0.04 : 0.18;
    const reverted = nextValue + (t.baseline - nextValue) * revertStrength;
    const pct = ((reverted - t.baseline) / t.baseline) * 100;
    const flag = classify(pct);
    return { ...t, value: reverted, pct, flag, injectedTicks: nextInjectedTicks };
  });
}

// ── Phase 25.16f — 7-tile snapshot encoder/decoder ──────────────────
// Freeze the current state of all 7 tiles into a base64-encoded URL
// param. Schema v2: {
//   v: 2,
//   t: timestamp,
//   tiles: { profileId: [[tagId, value],...] }
// }
// v2 drops the brief text from the payload — briefs are deterministic
// from (tag values × profileId × siteName) via buildBrief(), so we
// recompute them at decode time. Trims the URL from ~6KB to ~1.8KB,
// safely under the ~2KB cliff that some email clients (Outlook) hit.
// v1 payloads (with `.b` brief text + `.x` tuple list) still decode
// correctly via legacy-aware decoder below.
function encode7Snapshot(tilesState) {
  const tiles = {};
  Object.keys(tilesState).forEach(pid => {
    const arr = tilesState[pid];
    if (!arr) return;
    tiles[pid] = arr.map(t => [t.id, Number(t.value.toFixed(2))]);
  });
  const payload = { v: 2, t: Date.now(), tiles };
  try {
    const json = JSON.stringify(payload);
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return null;
  }
}

function decode7Snapshot(encoded) {
  try {
    const json = decodeURIComponent(escape(atob(encoded)));
    const payload = JSON.parse(json);
    if (!payload.tiles) return null;
    // v2: tiles is { profileId: [[tagId, value],...] } — normalise to
    //     { profileId: { x: tuples } } so the hydrator has one shape.
    // v1: tiles is { profileId: { x, b } } — pass through.
    if (payload.v === 2) {
      const normalised = {};
      Object.keys(payload.tiles).forEach(pid => {
        normalised[pid] = { x: payload.tiles[pid] };
      });
      return { v: 2, t: payload.t, tiles: normalised };
    }
    if (payload.v === 1) {
      return payload;
    }
    return null;
  } catch {
    return null;
  }
}

// Re-hydrate a flat tag array from a [[tagId, value],...] tuple list.
// Looks up each tag's metadata from SITE_PROFILES[profileId] so unit /
// baseline / range / source are preserved even though only [id, value]
// were stored in the URL.
function hydrateTilesFromSnapshot(tilesPayload) {
  const result = {};
  Object.keys(tilesPayload).forEach(pid => {
    const profile = SITE_PROFILES[pid];
    if (!profile) return;
    const tuples = tilesPayload[pid]?.x || [];
    const valueByTag = Object.fromEntries(tuples);
    result[pid] = profile.tags.map(t => {
      const v = valueByTag[t.id];
      if (v == null) {
        return {
          ...t, value: t.baseline, pct: 0, flag: classify(0),
          injectedTicks: 0, injectedDirection: 1,
        };
      }
      const pct = ((v - t.baseline) / t.baseline) * 100;
      return {
        ...t, value: v, pct, flag: classify(pct),
        injectedTicks: 0, injectedDirection: 1,
      };
    });
  });
  return result;
}

export default function Ventia7SectorDemo() {
  usePageview("/ventia/demo");
  useNoIndex();

  // Phase 25.16f — Replay mode: hydrate from URL on first render.
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotParam = searchParams.get("snapshot");
  const replayInfo = snapshotParam ? decode7Snapshot(snapshotParam) : null;
  const isReplay = Boolean(replayInfo);

  const [running, setRunning] = useState(!isReplay);
  const [tickCount, setTickCount] = useState(0);

  // tilesState: { [profileId]: tagsArr }
  const [tilesState, setTilesState] = useState(() => {
    if (replayInfo?.tiles) {
      return hydrateTilesFromSnapshot(replayInfo.tiles);
    }
    const init = {};
    SECTOR_TILES.forEach(t => {
      const p = SITE_PROFILES[t.profileId];
      if (p) init[t.profileId] = freshTagState(p);
    });
    return init;
  });

  // briefs: { [profileId]: string }
  const [briefs, setBriefs] = useState(() => {
    if (replayInfo?.tiles) {
      const hydratedTiles = hydrateTilesFromSnapshot(replayInfo.tiles);
      const init = {};
      SECTOR_TILES.forEach(t => {
        const p = SITE_PROFILES[t.profileId];
        const arr = hydratedTiles[t.profileId];
        if (p && arr) {
          // v2 payloads have no brief text — recompute deterministically
          // from the hydrated tag values + sector profile.
          // v1 payloads carry brief text in tiles[pid].b — prefer that
          // if present, since it was the exact text shown at save time.
          const stored = replayInfo.tiles[t.profileId]?.b;
          init[t.profileId] = stored || buildBrief(arr, p.name, t.profileId);
        }
      });
      return init;
    }
    const init = {};
    SECTOR_TILES.forEach(t => {
      const p = SITE_PROFILES[t.profileId];
      if (!p) return;
      init[t.profileId] = buildBrief(freshTagState(p), p.name, t.profileId);
    });
    return init;
  });

  // tilesStateRef — kept in sync so the brief-rebuild effect can read the
  // latest tag values WITHOUT depending on tilesState (which would force
  // brief regeneration on every 1.5s tick — too jumpy to read).
  const tilesStateRef = useRef(tilesState);
  tilesStateRef.current = tilesState;

  // briefRefreshSignal — bumped every 6 ticks by the tick loop AND
  // immediately after an inject. Drives a separate effect that reads
  // tilesStateRef and rebuilds all 7 briefs at once.
  const [briefRefreshSignal, setBriefRefreshSignal] = useState(0);
  const briefTickRef = useRef(0);

  // Tick loop drives all 7 tiles + bumps the brief signal every 6 ticks.
  // Suspended entirely during replay mode — the URL is the source of truth.
  useEffect(() => {
    if (!running || isReplay) return undefined;
    const id = setInterval(() => {
      setTickCount(c => c + 1);
      setTilesState(prev => {
        const next = {};
        Object.keys(prev).forEach(pid => {
          next[pid] = tickTags(prev[pid]);
        });
        return next;
      });
      briefTickRef.current += 1;
      if (briefTickRef.current % 6 === 0) {
        setBriefRefreshSignal(s => s + 1);
      }
    }, 1500);
    return () => clearInterval(id);
  }, [running, isReplay]);

  // Rebuild all briefs from the latest tilesStateRef snapshot whenever
  // the brief refresh signal bumps. Skipped in replay mode (briefs are
  // already hydrated from the URL payload).
  useEffect(() => {
    if (isReplay) return;
    const current = tilesStateRef.current;
    const nextBriefs = {};
    SECTOR_TILES.forEach(t => {
      const p = SITE_PROFILES[t.profileId];
      const arr = current[t.profileId];
      if (p && arr) {
        nextBriefs[t.profileId] = buildBrief(arr, p.name, t.profileId);
      }
    });
    setBriefs(nextBriefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefRefreshSignal]);

  const totalFlagged = Object.values(tilesState).reduce(
    (sum, arr) => sum + arr.filter(t => t.flag.tone !== "ok").length,
    0,
  );
  const totalTags = Object.values(tilesState).reduce((sum, arr) => sum + arr.length, 0);

  // Track pending brief-bump timers so they can be cleared on unmount —
  // prevents React's "state update on unmounted component" warning if
  // the user navigates away mid-bump.
  const pendingBumpTimers = useRef([]);
  useEffect(() => () => {
    pendingBumpTimers.current.forEach(clearTimeout);
    pendingBumpTimers.current = [];
  }, []);

  const injectRandomEverywhere = () => {
    setTilesState(prev => {
      const next = {};
      Object.keys(prev).forEach(pid => {
        const arr = prev[pid];
        const idx = Math.floor(Math.random() * arr.length);
        const dir = Math.random() > 0.5 ? 1 : -1;
        next[pid] = arr.map((t, i) => (
          i === idx ? { ...t, injectedTicks: 3, injectedDirection: dir } : t
        ));
      });
      return next;
    });
    // Wait two ticks (~3s) so injectionOffset has actually propagated
    // through tickTags() and the new values + flags are visible. Then
    // bump the brief signal so the rebuild effect picks up the latest
    // snapshot via tilesStateRef.
    pendingBumpTimers.current.push(
      setTimeout(() => setBriefRefreshSignal(s => s + 1), 1800),
      setTimeout(() => setBriefRefreshSignal(s => s + 1), 3200),
    );
    toast.success("Injected anomalies across all 7 sectors");
  };

  // Phase 25.16f — Snapshot save. Freezes the live tag values of all 7
  // tiles into a base64 URL param. Brief text is NOT stored in the URL
  // (v2 payload format) — it's recomputed deterministically on decode
  // from the hydrated tag values via buildBrief(). Trims the URL from
  // ~6KB to ~1.8KB, comfortably under Outlook's 2KB truncation cliff.
  //
  // To keep the sender's on-screen view identical to what the recipient
  // sees when they open the URL, we first ROUND live tilesState values
  // to 2 decimals (the same precision that gets serialised into the
  // URL), then compute briefs from the rounded state, then commit both
  // to React state. This eliminates the float-precision drift that
  // would otherwise nudge percentages by 0.1 between save and reload.
  const saveSnapshot = () => {
    const roundedTiles = {};
    Object.keys(tilesState).forEach(pid => {
      const arr = tilesState[pid];
      if (!arr) return;
      roundedTiles[pid] = arr.map(t => {
        const rv = Number(t.value.toFixed(2));
        const pct = ((rv - t.baseline) / t.baseline) * 100;
        return { ...t, value: rv, pct, flag: classify(pct) };
      });
    });
    const freshBriefs = {};
    SECTOR_TILES.forEach(t => {
      const p = SITE_PROFILES[t.profileId];
      const arr = roundedTiles[t.profileId];
      if (p && arr) {
        freshBriefs[t.profileId] = buildBrief(arr, p.name, t.profileId);
      }
    });
    setTilesState(roundedTiles);
    setBriefs(freshBriefs);
    const encoded = encode7Snapshot(roundedTiles);
    if (!encoded) {
      toast.error("Snapshot failed to encode");
      return;
    }
    const url = `${window.location.origin}/ventia/demo?snapshot=${encoded}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Snapshot URL copied · paste into the email to Shaun"),
      () => toast.error("Couldn't copy — URL is in the address bar instead"),
    );
    setSearchParams({ snapshot: encoded }, { replace: true });
  };

  // Phase 25.16f — Exit replay mode. Clear the URL param; React will
  // pick that up on the next render, isReplay flips false, the tick
  // loop re-arms, and briefs start regenerating from a fresh state.
  const resumeLive = () => {
    setSearchParams({}, { replace: true });
    // Reset to baseline so the resumed stream starts clean instead of
    // continuing from the frozen-snapshot values.
    const fresh = {};
    const freshBriefs = {};
    SECTOR_TILES.forEach(t => {
      const p = SITE_PROFILES[t.profileId];
      if (!p) return;
      fresh[t.profileId] = freshTagState(p);
      freshBriefs[t.profileId] = buildBrief(fresh[t.profileId], p.name, t.profileId);
    });
    setTilesState(fresh);
    setBriefs(freshBriefs);
    setRunning(true);
    setBriefRefreshSignal(s => s + 1);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ventia-7sector-demo">
      {/* Phase 25.16f — Replay-mode banner, visible only when hydrated
          from a snapshot URL. Lets the recipient instantly re-arm the
          live demo with one click. */}
      {isReplay && (
        <div
          className="bg-[#FFB000] text-black px-4 sm:px-6 py-3 flex items-center justify-between flex-wrap gap-3"
          data-testid="ventia-7sector-replay-banner"
        >
          <div className="flex items-center gap-3">
            <Camera className="w-4 h-4 flex-shrink-0" />
            <div className="font-mono text-[11px] uppercase tracking-widest">
              REPLAY MODE · FROZEN 7-SECTOR SNAPSHOT
              {replayInfo?.t && (
                <span className="ml-3 normal-case opacity-70">
                  captured {new Date(replayInfo.t).toLocaleString("en-AU", {
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={resumeLive}
            data-testid="ventia-7sector-resume-live"
            className="font-mono text-[10px] uppercase tracking-widest bg-black text-[#FFB000] px-3 py-1.5 hover:opacity-90 flex items-center gap-1.5"
          >
            <Play className="w-3 h-3" /> Resume live demo
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/ventia" className="flex items-center gap-2 group" data-testid="back-to-ventia">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · VENTIA · 7-SECTOR LIVE DEMO
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link
              to="/ventia"
              data-testid="nav-back-to-proposal"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] hidden sm:inline-flex items-center gap-1.5"
            >
              <ArrowRight className="w-3 h-3 rotate-180" /> Bespoke proposal
            </Link>
            <Link
              to="/industrial-demo"
              data-testid="nav-deep-dive"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 flex items-center gap-1.5"
            >
              <ExternalLink className="w-3 h-3" /> Single-sector deep dive
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 sm:py-14 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">
            // LIVE DEMO · 7 VENTIA SERVICE LINES · STREAMING IN PARALLEL
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-5xl">
            Seven sectors. <span className="text-[#FFB000]">One screen.</span> Live.
          </h1>
          <p className="text-sm sm:text-base text-zinc-300 mt-4 max-w-3xl leading-relaxed">
            Every Ventia service line, each running its own simulated tier-1 site, each ticking every 1.5 seconds, each generating its own sector-specific operations brief. Read-only ingest pattern — same architecture as the real DARA Engine. <span className="text-zinc-500">All telemetry simulated client-side; no real customer data.</span>
          </p>

          {/* Status band */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900" data-testid="ventia-7sector-status">
            <StatusCell label="ACTIVE SECTORS" value="7 / 7" />
            <StatusCell label="STREAMING TAGS" value={`${totalTags}`} />
            <StatusCell label="UPDATE INTERVAL" value="1.5 s" />
            <StatusCell label="TICKS · THIS SESSION" value={tickCount} />
            <StatusCell label="FLAGGED · NOW" value={`${totalFlagged} / ${totalTags}`} highlight={totalFlagged > 0} accent />
          </div>

          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
              <span className={`w-2 h-2 rounded-full ${running && !isReplay ? "bg-[#00E676] animate-pulse" : "bg-zinc-600"}`} />
              <span className="text-zinc-400">
                {isReplay
                  ? "STREAM FROZEN · REPLAY"
                  : running
                  ? "STREAMING · LIVE · ALL SECTORS"
                  : "STREAM PAUSED"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setRunning(r => !r)}
                disabled={isReplay}
                data-testid="ventia-7sector-toggle"
                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#FFB000] hover:text-[#FFB000] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {running ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Resume</>}
              </button>
              <button
                type="button"
                onClick={injectRandomEverywhere}
                disabled={!running || isReplay}
                data-testid="ventia-7sector-inject-all"
                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 hover:bg-[#FFB000]/15 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                title="Force a +/-35% anomaly on one random tag in every sector simultaneously"
              >
                <Zap className="w-3 h-3" /> Inject anomalies across all 7
              </button>
              <button
                type="button"
                onClick={saveSnapshot}
                disabled={isReplay}
                data-testid="ventia-7sector-save-snapshot"
                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#FFB000] hover:text-[#FFB000] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                title="Freeze this exact moment across all 7 sectors into a shareable URL"
              >
                <Camera className="w-3 h-3" /> Save snapshot
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 7-tile grid */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ventia-7sector-grid">
            {SECTOR_TILES.map(tile => {
              const profile = SITE_PROFILES[tile.profileId];
              const tags = tilesState[tile.profileId] || [];
              const brief = briefs[tile.profileId] || "";
              return (
                <SectorTile
                  key={tile.profileId}
                  tile={tile}
                  profile={profile}
                  tags={tags}
                  brief={brief}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer — proof points + CTA */}
      <section className="border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ProofPoint
              label="READ-ONLY"
              value="No PLC writes. No actuation. DARA reads, reconciles, reports."
            />
            <ProofPoint
              label="SAME ARCHITECTURE"
              value="What's running on this page is the same engine that runs on real sites. Switching to a real OPC UA / OSIsoft PI / Modbus feed is a connector swap."
            />
            <ProofPoint
              label="AU-SOVEREIGN"
              value="Tenant-isolated, AES-256 at rest, TLS 1.3 in transit, IRAP-aligned for non-classified workloads (Defence sector). Australian cloud only."
            />
          </div>
          <div className="mt-10 flex items-center justify-between flex-wrap gap-4 border border-[#FFB000]/30 bg-[#FFB000]/5 px-6 py-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">// NEXT STEP</div>
              <div className="text-base sm:text-lg font-bold tracking-tight">Walk through any single sector at depth.</div>
              <div className="text-xs text-zinc-400 mt-1">Open the single-sector demo for the full tag listing, the brief-history panel, and the shareable-snapshot capture.</div>
            </div>
            <Link
              to="/industrial-demo"
              data-testid="footer-deep-dive-cta"
              className="font-mono text-[10px] uppercase tracking-widest bg-[#FFB000] text-black px-4 py-2.5 hover:opacity-90 flex items-center gap-1.5"
            >
              Open deep-dive <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom strip */}
      <div className="bg-black border-t border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between flex-wrap gap-3">
          <span>// DARA OS · BESPOKE FOR VENTIA · {new Date().toLocaleString("en-AU", { month: "short", year: "numeric" }).toUpperCase()}</span>
          <Link to="/ventia" className="hover:text-[#FFB000] flex items-center gap-1.5" data-testid="bottom-back-to-proposal">
            <ArrowRight className="w-3 h-3 rotate-180" /> Back to bespoke proposal
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Tile component ─────────────────────────────────────────────────
function SectorTile({ tile, profile, tags, brief }) {
  const { Icon } = tile;
  if (!profile) return null;
  const flagged = tags.filter(t => t.flag.tone !== "ok");
  const flaggedCount = flagged.length;
  const tone = flaggedCount === 0 ? "ok" : flagged.some(t => t.flag.tone === "high") ? "high" : "watch";

  const toneColour = {
    ok: { border: "border-zinc-800", pulse: "bg-zinc-700", text: "text-zinc-400", label: "NOMINAL" },
    watch: { border: "border-[#FFB000]/50", pulse: "bg-[#FFB000]", text: "text-[#FFB000]", label: "DRIFT" },
    high: { border: "border-[#FF6B47]/60", pulse: "bg-[#FF6B47]", text: "text-[#FF6B47]", label: "ANOMALY" },
  }[tone];

  return (
    <div
      className={`bg-black border ${toneColour.border} transition-colors`}
      data-testid={`ventia-tile-${tile.profileId}`}
    >
      {/* Tile header */}
      <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-[#FFB000] flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
              SECTOR {tile.sector} · {profile.sector_label || profile.short}
            </div>
            <div className="text-sm text-white truncate" title={profile.name}>
              {profile.name}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`w-2 h-2 rounded-full ${toneColour.pulse} ${tone !== "ok" ? "animate-pulse" : ""}`} />
          <span className={`font-mono text-[9px] uppercase tracking-widest ${toneColour.text}`}>
            {toneColour.label}
          </span>
        </div>
      </div>

      {/* Tag mini-table */}
      <div className="px-4 py-3 border-b border-zinc-900">
        <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-600 mb-2">
          // {tags.length} TAGS · {flaggedCount} FLAGGED
        </div>
        <div className="space-y-1">
          {tags.slice(0, 5).map(t => (
            <TagRow key={t.id} tag={t} />
          ))}
          {tags.length > 5 && (
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 pt-1">
              + {tags.length - 5} more tags
            </div>
          )}
        </div>
      </div>

      {/* Mini brief */}
      <div className="px-4 py-3 border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="font-mono text-[8px] uppercase tracking-widest text-[#FFB000] mb-1.5 flex items-center gap-1.5">
          <Activity className="w-2.5 h-2.5" /> AUTO-BRIEF · 06:00 SYNTHESIS
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed line-clamp-4" data-testid={`ventia-tile-brief-${tile.profileId}`}>
          {brief}
        </p>
      </div>

      {/* Deep-dive link */}
      <Link
        to={`/industrial-demo?profile=${tile.profileId}`}
        data-testid={`ventia-tile-deepdive-${tile.profileId}`}
        className="px-4 py-2.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] hover:bg-[#FFB000]/5 transition-colors"
      >
        <span>Open deep-dive · sector {tile.sector}</span>
        <ChevronRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function TagRow({ tag }) {
  const tone = tag.flag.tone;
  const toneColour = {
    ok: "text-zinc-400",
    watch: "text-[#FFB000]",
    high: "text-[#FF6B47]",
  }[tone];
  const dotColour = {
    ok: "bg-zinc-700",
    watch: "bg-[#FFB000]",
    high: "bg-[#FF6B47]",
  }[tone];
  return (
    <div className="flex items-center justify-between gap-2 font-mono text-[10px]">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className={`w-1.5 h-1.5 rounded-full ${dotColour} flex-shrink-0`} />
        <span className="text-zinc-400 truncate" title={tag.label}>{tag.label}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`${toneColour} font-bold`}>
          {fmtNum(tag.value, tag.unit)}<span className="text-zinc-600 font-normal ml-0.5">{tag.unit}</span>
        </span>
        {tone !== "ok" && (
          <span className={`${toneColour} tabular-nums w-12 text-right`}>
            {tag.pct > 0 ? "+" : ""}{tag.pct.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

function StatusCell({ label, value, highlight = false, accent = false }) {
  return (
    <div className={`px-3 py-3 bg-black flex flex-col justify-center ${accent ? "border-l-2 border-[#FFB000]" : ""}`}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
        // {label}
      </div>
      <div className={`text-sm font-bold tracking-tight ${highlight ? "text-[#FF6B47]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function ProofPoint({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// {label}</div>
      <div className="text-sm text-zinc-300 leading-relaxed">{value}</div>
    </div>
  );
}
